'use client';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ERROR_TYPES, reportUxError, trackErrorRetry, type ErrorType } from '@/lib/analytics/error-telemetry';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import { humanizeError, UX_LEXICON } from '@/lib/ux-lexicon';
import { semanticTokens } from '@packages/config/theme';
import * as React from 'react';

export interface ErrorStateProps {
  /**
   * Error title (optional, will use humanized error if not provided)
   */
  title?: string;

  /**
   * Error message (required)
   */
  message: string;

  /**
   * Retry callback
   */
  onRetry?: () => void;

  /**
   * Retry button label (default: "Retry")
   */
  retryLabel?: string;

  /**
   * Custom icon (optional, defaults to AlertTriangle)
   */
  icon?: React.ReactNode;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Visual variant
   */
  variant?: 'default' | 'destructive';

  /**
   * Error type for analytics (from ERROR_TYPES taxonomy)
   */
  errorType?: ErrorType | string;

  /**
   * Surface where error is shown (e.g., 'dashboard', 'wallet', 'transactions')
   */
  surface?: string;

  /**
   * Whether this is a critical error (affects analytics)
   */
  critical?: boolean;

  /**
   * Raw error object (for humanization)
   */
  error?: unknown;

  /**
   * Secondary action (e.g., "Contact support")
   */
  secondaryAction?: {
    label: string;
    onClick: () => void;
    variant?: 'default' | 'outline' | 'ghost' | 'destructive';
  };
}

/**
 * ErrorState Component
 * 
 * Canonical error display component for user platform.
 * Uses UX_LEXICON for human-readable messages and tracks analytics.
 * 
 * @example
 * ```tsx
 * <ErrorState
 *   message="Failed to load data"
 *   errorType="wallet_balances_failed"
 *   surface="wallet"
 *   onRetry={() => refetch()}
 * />
 * ```
 * 
 * @example With error object (auto-humanization)
 * ```tsx
 * <ErrorState
 *   error={error}
 *   errorType="tx_history_failed"
 *   surface="transactions"
 *   onRetry={() => refetch()}
 * />
 * ```
 */
export function ErrorState({
  title,
  message,
  onRetry,
  retryLabel = 'Retry',
  icon,
  className,
  variant = 'destructive',
  errorType = 'unknown_error',
  surface = 'unknown',
  critical = false,
  error,
  secondaryAction,
}: ErrorStateProps) {
  // Humanize error if error object provided
  const humanized = React.useMemo(() => {
    if (error) {
      return humanizeError(error);
    }
    return null;
  }, [error]);

  const displayTitle = title || humanized?.title || UX_LEXICON.errors.unknown.title;
  const displayMessage = humanized?.message || message || UX_LEXICON.errors.unknown.message;

  // Normalize error type
  const normalizedErrorType = (errorType || ERROR_TYPES.UNKNOWN_ERROR) as ErrorType;

  // Track error shown event using centralized helper
  React.useEffect(() => {
    reportUxError(error || new Error(displayMessage), {
      type: normalizedErrorType,
      surface,
      critical,
      metadata: error ? { error_message: displayMessage } : undefined,
    });
  }, [error, normalizedErrorType, surface, critical, displayMessage]);

  const handleRetry = React.useCallback(() => {
    // Track retry click using centralized helper
    trackErrorRetry(normalizedErrorType, surface);

    // Call retry callback
    onRetry?.();
  }, [onRetry, normalizedErrorType, surface]);

  return (
    <div
      className={cn(
        'py-8',
        semanticTokens.motion.standard,
        className
      )}
      data-testid="error-state"
      data-error-type={errorType}
      aria-live="assertive"
      role="alert"
    >
      <Alert variant={variant}>
        {icon || (
          <sfIcons.alertTriangle
            className={cn('h-4 w-4', semanticTokens.danger.accent)}
            aria-hidden="true"
          />
        )}
        <AlertDescription>
          <div className={cn('space-y-2')}>
            <p className={cn('font-medium', semanticTokens.typography.h4)}>
              {displayTitle}
            </p>
            <p className={cn('text-sm', semanticTokens.fg.muted)}>
              {displayMessage}
            </p>
            {(onRetry || secondaryAction) && (
              <div className={cn('mt-2 flex flex-col sm:flex-row gap-2')}>
                {onRetry && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRetry}
                    className={cn(
                      'inline-flex items-center gap-2',
                      semanticTokens.motion.standard,
                      'focus-visible:ring-2 focus-visible:ring-blue-500'
                    )}
                    data-testid="error-retry-button"
                  >
                    <sfIcons.refresh className="h-4 w-4" aria-hidden="true" />
                    {retryLabel}
                  </Button>
                )}
                {secondaryAction && (
                  <Button
                    variant={secondaryAction.variant || 'outline'}
                    size="sm"
                    onClick={secondaryAction.onClick}
                    className={cn(
                      semanticTokens.motion.standard,
                      'focus-visible:ring-2 focus-visible:ring-blue-500'
                    )}
                    data-testid="error-secondary-action-button"
                  >
                    {secondaryAction.label}
                  </Button>
                )}
              </div>
            )}
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}
